import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { getLocale, useIntl, useSelector } from 'umi';
import moment from 'moment';
import {
  Card,
  Segment,
  Bubble,
  Justify,
  Col,
  Row,
  Select,
  Radio,
} from 'tea-component';
import { OverflowBubbleText } from '@/pages/dashboard/components/OverflowBubbleText';

import SvgIcon from '@/pages/home/components/SvgIcon';
import { useLinkPath } from '@/pages/dashboard/utils/customHook';
import useItem from './useItem';

import MiniTable from '@/pages/dashboard/components/MiniTable';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import {
  addChartLabel,
  changeMin,
  compareTrend,
  xAxisHighLight,
} from '@/pages/dashboard/components/Chart/_config/plug';
import {
  formatNum,
  formatTruNum,
  unitConversion,
  getIsErrorValue,
  unitConversionNew,
  getDateFormatString,
  getSystemColor,
  formatSmallValue,
  formatSmallPercent,
  jumpDetail,
} from '@/pages/dashboard/utils/utils';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import getSystemLegend from "@/pages/dashboard/components/CountryRegionRowItem/CountryTabChartsItem/utils";
import EventDetailsPop, { useEventDetailsPop } from '@/pages/dashboard/pages/eventAnalysis/EventDetailsPop';
import useOverviewLocalesIntl from '../../_config/useOverviewLocalesIntl';
import { cloneDeep } from 'lodash-es';
import { getCustomValueColumnTooltip } from '@/pages/dashboard/components/EchartsTooltip/utils';

import '@/pages/dashboard/assets/scss/page/Overlay/components/tableCard.scss';
import './index.scss';
import ACard from '@/pages/dashboard/components/ACard';
import { ARPU_TYPES } from '../../_config/constant';
import {
  IChartItemProps,
  IChartOverlaySlotContext,
} from '@/pages/dashboard/pages/overview/components/OverviewMetricsChart/type';
import { getAggregateValues } from '@/pages/dashboard/pages/overview/components/OverviewMetricsChart/utils';
import { LANGUAGE_EN } from '@home/_config/config';
import { OverviewMetricsChartHeader } from '@/pages/dashboard/pages/overview/components/OverviewMetricsChart/components/OverviewMetricsChartHeader';
import ViewportFullscreenTip from '@/pages/dashboard/components/ViewportFullscreenTip';
import classNames from 'classnames';

/** 全屏 EventWindows 定位用占位 series，写入 option 而非事后 setOption */
function appendTopTipsEventForOverlay(opt: Record<string, unknown>): Record<string, unknown> {
  const xAxis = opt.xAxis as
    | { data?: unknown[] }
    | Array<{ data?: unknown[] }>
    | undefined;
  const xData = Array.isArray(xAxis)
    ? xAxis[0]?.data ?? []
    : xAxis?.data ?? [];
  const xLen = Array.isArray(xData) ? xData.length : 0;
  const series = Array.isArray(opt.series)
    ? [...(opt.series as Array<{ name?: string }>)]
    : [];
  if (series.some(s => String(s?.name ?? '') === 'TopTipsEvent')) {
    return opt;
  }
  series.push({
    name: 'TopTipsEvent',
    type: 'line',
    data: xLen > 0 ? Array(xLen).fill(null) : [],
    silent: true,
    symbol: 'none',
    lineStyle: { opacity: 0 },
    itemStyle: { opacity: 0 },
  });
  return { ...opt, series };
}

export default function ChartItem(props: IChartItemProps) {
  const { formatMessage } = useIntl();
  const {
    config,
    tabList,
    type,
    renderIndex,
    formValue,
    separatedOption,
    error,
    eventData,
    placeholderId = '',
    regionDataType,
    gameCode,
    isMoblie: isMobile = false,
    visibleLegends,
    aggregateData,
    viewportFullscreen,
    /** 全屏时注入 KpiEventFilter：eventFilterBesideTitle 为 true 贴标题，否则在维度下拉右侧 */
    leftExtraSlot,
    /** 叠加在 chart 区域（.dbd-overlay-chart）内部的绝对定位 overlay；外层用于渲染 EventWindows 浮窗，不影响其他用例 */
    chartOverlaySlot,
    showSegmentTabsInFullScreen = false,
    eventFilterBesideTitle = false,
    eventTypeMaxCount,
    filterLoading = false,
  } = props;

  const DEFAULT_CHART_COM_TYPE = 'discount';
  const persistKeyComType = `overviewMetricsComType_${renderIndex}`;
  const persistKeyIsEye = `overviewMetricsIsEye_${renderIndex}`;
  const persistKeyShowChangeTag = `overviewMetricsShowChangeTag_${renderIndex}`;

  // 三行高度阈值
  const SEGMENT_THREE_ROW_HEIGHT = 135;

  const popRef = useRef();
  const lineChartRef = useRef();
  const barChartRef = useRef();
  const segmentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const lineChartObj = lineChartRef?.current?.instance();
  const { selector } = useSelector((state: any) => state?.dashboardSetting ?? {});
  const resizeNum = useRef(0);
  const prevHeightRef = useRef<number>(300);
  // const segementHeightRef = useRef(document.getElementsByClassName(`daily-segment-${renderIndex}`)?.[0]?.clientHeight || 0);
  // 事件详情数据
  const { currentEvents, slotConfigFunc } = useEventDetailsPop(
    eventData,
    popRef,
  );
  const linkPath = useLinkPath('eventAnalysis/eventDetails');

  const eventSlotConfigFunc = useCallback(
    (opt, chart) => {
      const options = slotConfigFunc(opt, chart);
      options.yAxis = [
        options.yAxis,
        {
          show: false,
          axisLabel: {
            formatter() {
              return "";
            }
          }
        },
      ];
      opt.grid.x2 = 2;
      options.series[options.series.length - 1].yAxisIndex = 1;
      return options;
    },
    [slotConfigFunc],
  );

  const segementList = useMemo(() => {
    const segementList = cloneDeep(tabList);
    // 给segment项加上指标解释： 日 WD2 WD3 WD7  周 WW2 WW3  月 WM2 WM3
    for (const iterator of segementList) {
      if (iterator?.bubble) {
        iterator.text = (
          <Bubble className="segment-bubble" dark content={<div dangerouslySetInnerHTML={{ __html: iterator?.bubble }}></div>}>
            {iterator?.text}
          </Bubble>
        );
        delete iterator.bubble;
      }
    }
    return segementList;
  }, [tabList]);

  const newprops = useItem(props);
  const {
    data,
    loading,
    dataType,
    selected,
    userQuota,
    hasPercentList,
    getWeekDayStr,
    downloadData,
    onDataTypeChange,
    LEGEND_MAP, // 接收 LEGEND_MAP
    dataTypeText, // 接收 dataTypeText
  } = newprops;

  const isViewportFullscreenEnterDisabled = Boolean(loading || filterLoading);

  const isArpuType = (type: string) => ARPU_TYPES.includes(type);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectText, setSelectText] = useState(
    formatMessage({ id: 'common_Accumulated' }),
  );
  const [selectedValue, setSelectedValue] = useState(selected);
  const [showChangeTag, setShowChangeTagState] = useState(() => {
    const snap = viewportFullscreen?.getChildSortSnapshot?.(persistKeyShowChangeTag);
    return typeof snap === 'boolean' ? snap : false;
  });
  const setShowChangeTag = (value: boolean | ((prev: boolean) => boolean)): void => {
    setShowChangeTagState(prev => {
      const next = typeof value === 'function' ? value(prev) : value;
      viewportFullscreen?.setChildSortSnapshot?.(persistKeyShowChangeTag, next);
      return next;
    });
  };
  const { CHART_TYPE, DATE_DIMENSION } = useOverviewLocalesIntl();

  const [dheight, setdheight] = useState(300);
  // const [isWrap, setWrap] = useState(true);
  const {
    showVs = true,
    showTitle = false,
    dimensionOptions = [],
    aggregateConfig = {},
  } = config || {};

  /** 当前选中 tab 的后端的指标名称 */
  const currentBackendMetric = dataType;
  /** 当前选中的 tab */
  const currentTab = tabList?.find((tab) => tab.value === dataType);

  /**
   * 计算聚合副标题 (SUM / AVG)
   * 参考 SalesTrendChart 的 subTitle 实现
   */
  const aggregateSubTitle = useMemo(() => {
    if (!aggregateData) return [];
    const {
      sum,
      avg,
      max,
      min,
      showMetricsNameOnFirstAggregateType,
    } = aggregateConfig || {};
    const typeMap = aggregateConfig ? {
      sum: sum?.type,
      avg: avg?.type,
      max: max?.type,
      min: min?.type,
    } : undefined;
    const { sum: sumValue, avg: avgValue, max: maxValue, min: minValue } = getAggregateValues(aggregateData ?? [], currentBackendMetric, typeMap);

    const metricName = currentTab?.text ?? '';
    const isUser = userQuota?.includes(dataType);
    const isEN = getLocale() === LANGUAGE_EN;
    const items: { name: string; num: string; }[] = [];

    const buildLabel = (
      itemConfig: { labelEn?: string; labelCn?: string; showMetricName?: boolean; } | undefined,
      fallbackId: string,
    ): string => {
      const label = itemConfig
        ? (isEN ? (itemConfig.labelEn ?? '') : (itemConfig.labelCn ?? ''))
        : formatMessage({ id: fallbackId });
      const prefix = itemConfig?.showMetricName ? `${metricName} ` : '';
      return `${prefix}${label}`;
    };

    const shouldShowAggregate = (type: string): boolean => {
      return (aggregateConfig?.[type]?.show && !aggregateConfig?.[type]?.excludedMetrics?.includes(currentTab?.metricKey || ''));
    };

    /** SUM */
    if (shouldShowAggregate('sum')) {
      items.push({
        name: buildLabel(sum, 'pc_console_sum'),
        num: String(formatNum(sumValue ?? '', false, isUser)) || '-',
      });
    }

    /** AVG */
    if (shouldShowAggregate('avg')) {
      items.push({
        name: buildLabel(avg, 'pc_console_avg'),
        num: String(formatNum(avgValue ?? '', false, isUser)) || '-',
      });
    }

    /** MAX */
    if (shouldShowAggregate('max')) {
      items.push({
        name: buildLabel(max, 'pc_console_max'),
        num: String(formatNum(maxValue ?? '', false, isUser)) || '-',
      });
    }

    /** MIN */
    if (shouldShowAggregate('min')) {
      items.push({
        name: buildLabel(min, 'pc_console_min'),
        num: String(formatNum(minValue ?? '', false, isUser)) || '-',
      });
    }

    if (showMetricsNameOnFirstAggregateType) {
      const firstItem = items?.[0];
      if (firstItem && metricName) {
        if (!firstItem.name.startsWith(metricName) && 'name' in firstItem) {
          firstItem.name = `${metricName} ${firstItem.name}`;
        }
      }
    }

    return items;
  }, [aggregateData, aggregateConfig, currentBackendMetric, currentTab, userQuota, dataType, formatMessage]);

  const rightIcon = [
    {
      type: 'column',
      icon: 'column-chart',
      Bubble: formatMessage({ id: 'common_bar_chart' }),
    },
    {
      type: 'turn',
      icon: 'left-turn-right',
      Bubble: formatMessage({
        id: showChangeTag
          ? 'change_tag_bubble_show'
          : 'change_tag_bubble_hidden',
      }),
    },
    {
      type: 'discount',
      icon: 'discount-chart',
      Bubble: formatMessage({ id: 'common_line_chart' }),
    },
    {
      type: 'sheet',
      icon: 'sheet',
      Bubble: formatMessage({ id: 'common_table' }),
    },
    showVs && {
      type: 'vs',
      icon: 'vs-icon',
      Bubble: formatMessage({ id: 'common_comparison_analysis' }),
    },
  ]?.filter((v) => v !== false);
  const [comType, setComTypeState] = useState(() => {
    const snap = viewportFullscreen?.getChildSortSnapshot?.(persistKeyComType);
    return typeof snap === 'string' ? snap : (rightIcon[2]?.type ?? DEFAULT_CHART_COM_TYPE);
  });
  const setChartComType = (type: string): void => {
    viewportFullscreen?.setChildSortSnapshot?.(persistKeyComType, type);
    setComTypeState(type);
  };
  const [iseye, setIseyeState] = useState(() => {
    const snap = viewportFullscreen?.getChildSortSnapshot?.(persistKeyIsEye);
    return typeof snap === 'boolean' ? snap : true;
  });
  const setIseye = (value: boolean | ((prev: boolean) => boolean)): void => {
    setIseyeState(prev => {
      const next = typeof value === 'function' ? value(prev) : value;
      viewportFullscreen?.setChildSortSnapshot?.(persistKeyIsEye, next);
      return next;
    });
  };

  useEffect(() => {
    setSelectedValue(selected);
  }, [selected]);

  const formatterFunc = (param) => {
    // param?.pop(); // 删除事件详情数量的折线数据, 不需要展示在浮窗中
    if (!param?.length) return null;

    /**
     * 格式化日期（包含星期）
     */
    const formatDateWithWeekday = (date: moment.Moment): string => {
      if (type === DATE_DIMENSION.MONTH) {
        return date.format('YYYY-MM');
      }
      return `${date.format('YYYY-MM-DD')}(${date.format('ddd')})`;
    };

    /**
     * 计算日期区间（往前推N个周期，不包含当前日）
     * @example 当前14号，前7天 → 7号~13号
     */
    const calculateDateRange = (date: string, days: number, periodType: 'day' | 'week' | 'month'): string => {
      if (!date || !days) return date;
      
      const unit = periodType === 'day' ? 'days' : periodType === 'week' ? 'weeks' : 'months';
      const endDate = moment(date).subtract(1, unit);
      const startDate = endDate.clone().subtract(days - 1, unit);
      
      return `${formatDateWithWeekday(startDate)} ~ ${formatDateWithWeekday(endDate)}`;
    };

    /**
     * 获取自定义指标的日期区间配置
     */
    const getCustomDateRange = (seriesName: string, axisValue: string): string => {
      const legendObj = LEGEND_MAP?.[type] || {};
      
      for (const key in legendObj) {
        const legendItem = legendObj[key];
        const isVisibleLegend = visibleLegends && visibleLegends.length > 0;
        const legendName = isVisibleLegend ? legendItem?.name : `${dataTypeText} (${legendItem?.name})`;
        
        if (seriesName === legendName && legendItem?.dateRangeConfig) {
          return calculateDateRange(axisValue, legendItem.dateRangeConfig.days, legendItem.dateRangeConfig.periodType);
        }
      }
      return '';
    };

    const splitData = param?.map((item, index) => {
      let ecahrtValue =
        isArpuType(dataType)
          ? formatSmallValue(item?.value, 'decimals', 2)
          : hasPercentList?.includes(dataType)
            ? formatSmallPercent(item?.value, 2)
            : formatNum(item.value, false, userQuota.includes(dataType));
      if (getIsErrorValue(item.value)) ecahrtValue = '-';

      // 检查是否有自定义日期区间配置
      const customDateRange = getCustomDateRange(item.seriesName, item.axisValue);
      if (customDateRange) {
        return {
          title: customDateRange,
          data: [{ color: param[index].color, name: param[index].seriesName, metricsNum: ecahrtValue }],
        };
      }

      const isToday = item.seriesName.includes(formatMessage({ id: 'overview_daily_Today' })) || item.seriesName.includes(formatMessage({ id: 'overview_This_Week' })) || item.seriesName.includes(formatMessage({ id: 'overview_This_Month' }));
      const isSDLW = item.seriesName.includes(formatMessage({ id: 'overview_daily_Last_Week' }));
      const isSDLM = item.seriesName.includes(formatMessage({ id: 'overview_daily_Last_Month' }));
      const isSDLY = item.seriesName.includes(formatMessage({ id: 'overview_daily_Last_Year' }));

      const beforeTimeMap: any = {
        pcconsoleday: moment(item.axisValue).subtract(1, 'week').format('YYYY-MM-DD'),
        day: moment(item.axisValue).subtract(1, 'week').format('YYYY-MM-DD'),
        week: (() => {
          // 获取当前日期
          const currentDate = moment(item.axisValue);
          // 获取当前日期减去1个月的目标日期
          const targetDate = currentDate.clone().subtract(1, 'month');
          // 如果目标日期的日期部分和当前日期的日期部分相同，则返回目标日期的格式化结果，否则返回 "-"
          return currentDate.date() === targetDate.date() ? targetDate.format('YYYY-MM-DD') : "-";
        })(),
        month: (() => {
          const currentDate = moment(item.axisValue);
          const targetDate = currentDate.clone().subtract(1, 'year');
          return currentDate.date() === targetDate.date() ? targetDate.format(type === 'DAY' ? 'YYYY-MM-DD' : 'YYYY-MM') : "-";
        })(),
      };

      const now = getWeekDayStr(item.axisValue, type);
      const lastWeek = getWeekDayStr(beforeTimeMap.day, type);
      const lastMonth = getWeekDayStr(beforeTimeMap.week, type);
      const lastYear = getWeekDayStr(beforeTimeMap.month, type);
      const before = getWeekDayStr(
        beforeTimeMap[type?.toLocaleLowerCase()],
        type,
      );
      /** 当 visibleLegends 配置时，通过 key 判断是否为对比系列 */
      const isVisibleLegendMode = visibleLegends && visibleLegends?.length > 0;
      let title = before;
      if (isVisibleLegendMode) {
        const matchedLegend = LEGEND_MAP?.[type] && Object.values(LEGEND_MAP?.[type]).find((l: { name?: string; }) => item?.seriesName === l?.name);
        const legendKey = matchedLegend?.key || '';
        if (['last_week', 'last_value'].includes(legendKey)) title = lastWeek;
        else if (['last_month', 'last_month_value'].includes(legendKey)) title = lastMonth;
        else if (['last_year', 'last_year_value'].includes(legendKey)) title = lastYear;
        else title = now;
      } else {
        title =
          isToday ? now :
            isSDLW ? lastWeek :
              isSDLM ? lastMonth :
                isSDLY ? lastYear : before;
      }

      return {
        title,
        data: [
          {
            color: param[index].color,
            name: param[index].seriesName,
            metricsNum: ecahrtValue,
          },
        ],
      };
    });

    const title = type === 'MONTH' ? param?.[0]?.axisValue : getWeekDayStr(param?.[0]?.axisValue);
    const data = param?.map((item) => ({
      name: item?.seriesName,
      color: item?.color,
      metricsNum: hasPercentList?.includes(dataType) ? formatSmallPercent(item?.value, 2) : formatNum(item?.value, false, userQuota.includes(dataType)),
    }));

    return getEchartTooltipText({
      title,
      data,
      isSplit: true,
      isTwoDataCompare: isMobile && param?.length === 2,
      splitData,
    });
  };

  // 分维度 柱状图和折线图浮窗展示
  const dimensionFormatterFunc = (param) => {
    // param?.pop(); // 删除事件详情数量的折线数据, 不需要展示在浮窗中
    const metricsName = tabList.find((tab) => tab.value === dataType)?.text;

    // 走新浮窗处理
    return getCustomValueColumnTooltip({
      params: param, options: {
        hasSum: false,
        hasShare: false,
        valueNameCn: metricsName,
        valueNameEn: metricsName,
        isEn: getLocale() === 'en-US',
        isPlatform: regionDataType === 'os',
        valueFormatRule: '',
        formatFirstThText: (date) => getWeekDayStr(moment(date).format(type?.toLowerCase() === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD'), type)
      }
    }, (value) => {
      let metricsNumValue = value;
      if (getIsErrorValue(value)) metricsNumValue = '-';
      return hasPercentList?.includes(dataType) ? formatSmallPercent(metricsNumValue, 2) : formatNum(value, false, userQuota.includes(dataType));
    });
    // return getEchartTooltipText({
    //   title: getWeekDayStr(
    //     moment(param?.[0]?.axisValue).format(
    //       type?.toLowerCase() === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD',
    //     ),
    //     type,
    //   ),
    //   metricsName,
    //   isSort: true,
    //   data: param.map(({ seriesName, value, color }) => {
    //     let metricsNumValue = value;
    //     if (getIsErrorValue(value)) metricsNumValue = '-';
    //     return {
    //       name: seriesName,
    //       icon: regionDataType === 'os' ? getSystemFlag(seriesName) : '',
    //       metricsNum: hasPercentList?.includes(dataType)
    //         ? formatSmallPercent(metricsNumValue, 2)
    //         : formatNum(metricsNumValue, false, userQuota.includes(dataType)),
    //       color,
    //       customColor: regionDataType === 'os' && getSystemColor(seriesName.toLowerCase().replace(/\s/g, '')) || '',
    //     };
    //   }),
    // });
  };

  // 是否有数据
  const hasDataFlag = useMemo(() => {
    const series = data?.line?.series ?? [];

    // true = 没数据
    const isNoData = series.every(item => {
      const d = item?.data;

      // data 不存在 或 []
      if (!Array.isArray(d) || d.length === 0) {
        return true;
      }

      // data 存在，但全部是 ""
      return d.every(v => v === "");
    });

    return !isNoData;
  }, [data]);



  useEffect(() => {
    setSelectedValue(selected);
  }, [selected]);

  useEffect(() => {
    if (selectedValue === 'accumulated') {
      setSelectText(formatMessage({ id: 'common_Accumulated' }));
    } else {
      const selectItem: any = separatedOption?.find(
        (item: any) => item?.value === selectedValue,
      );
      setSelectText(selectItem?.text);
    }
  }, [getLocale(), selectedValue, separatedOption, formatMessage]);


  // icon点击跳转
  const iconClickfunc = (e: any) => {
    if (e.type === 'turn') {
      setShowChangeTag(!showChangeTag);
      return;
    }

    if (e.type === 'vs') {
      const indicators = dataType;
      const { diff, timeType, ...filters } = formValue || {};
      const newFormValue = {
        diff,
        timeType: timeType || 'Day',
        filterValue: {
          ...filters
        }
      };
      jumpDetail(`/dashboard/game/${gameCode}/comparisonAnalysis/dateComparison`, newFormValue, indicators);
      return;
    }
    e.type !== 'turn' && e.type !== 'vs' && setChartComType(e.type);
  };



  const barSeriesData = data?.[CHART_TYPE.COLUMN]?.series ?? [];
  const lineSeriesData = data?.[CHART_TYPE.LINE]?.series ?? [];

  // platform条形图自定义颜色
  const modifiedBarSeries = barSeriesData.map((series) => {
    const modifiedName = series.name.toLowerCase().replace(/\s/g, '');

    if (regionDataType === 'os' && getSystemColor(modifiedName)) {
      series.itemStyle = {
        color: getSystemColor(modifiedName),
      };
    }
    return series;
  });

  // platform折线图自定义颜色
  const modifiedLineSeries = lineSeriesData.map((series) => {
    const modifiedName = series.name.toLowerCase().replace(/\s/g, '');

    if (regionDataType === 'os' && getSystemColor(modifiedName)) {
      series.itemStyle = {
        color: getSystemColor(modifiedName),
      };
    }
    return series;
  });

  data[CHART_TYPE.COLUMN].series = modifiedBarSeries;
  data[CHART_TYPE.LINE].series = modifiedLineSeries;

  // ----------------------

  const injectTopTipsEventSeries = (opt: any, xAxisData: unknown[]) => {
    if (!chartOverlaySlot) return opt;
    const list = Array.isArray(xAxisData) ? xAxisData : [];
    const existing = Array.isArray(opt.series) ? opt.series : [];
    const hasSeries = existing.some((s: any) => s?.name === 'TopTipsEvent');
    if (hasSeries) return opt;
    opt.series = [
      ...existing,
      {
        name: 'TopTipsEvent',
        type: 'line',
        data: list.map(() => 0),
        showSymbol: false,
        symbol: 'none',
        silent: true,
        tooltip: { show: false },
        lineStyle: { opacity: 0 },
        itemStyle: { opacity: 0 },
        areaStyle: { opacity: 0 },
        z: -10,
      },
    ];
    return opt;
  };

  // line图表配置
  const lineChartOtherConfig = (opt: any) => {
    opt.legend.formatter = (name) => name?.replace(/[SDLW|SDLM|SDLY|SWLM|SMLY]+/g, (matchStr) => {
      const map = {
        SDLW: 'Same Day Last Week',
        SDLM: 'Same Day Last Month',
        SDLY: 'Same Day Last Year',
        SWLM: 'Same Week Last Month',
        SMLY: 'Same Month Last Year',
      };
      return map[matchStr] || matchStr;
    });
    opt.xAxis.boundaryGap = false;
    opt.xAxis.data = data?.[CHART_TYPE.LINE]?.xAxis ?? [];
    opt.xAxis[0].axisLabel.formatter = function (value) {
      const valueArr = value?.split('-');
      if (type === DATE_DIMENSION.MONTH) {
        return `${valueArr?.[0]}-${valueArr?.[1]}`;
      }
      return `${valueArr?.[1]}-${valueArr?.[2]}`;

    };
    Object.keys(data?.[CHART_TYPE.LINE]?.yAxis ?? {})?.forEach((key) => {
      if (typeof opt.yAxis[key] === 'object') {
        opt.yAxis[key] = {
          ...opt.yAxis[key],
          ...data?.[CHART_TYPE.LINE]?.yAxis[key],
        };
      } else {
        opt.yAxis[key] = data?.[CHART_TYPE.LINE]?.yAxis[key];
      }
    });
    if (selectedValue === 'accumulated') opt = compareTrend(opt);
    opt = addChartLabel(opt, {
      formatter: (params) => {
        if (isArpuType(dataType)) return formatSmallValue(params?.value, 'decimals', 2);
        const result =
          hasPercentList?.includes(dataType)
            ? formatSmallPercent(params?.value, 2)
            : formatNum(params?.value, false, userQuota.includes(dataType));
        if (!hasPercentList?.includes(dataType) && result?.length >= 9) {
          return unitConversionNew(params?.value, {
            isFloat: true,
            fixedNum: 3,
            fillZero: true,
          });
        }
        return result;
      },
      show: iseye && selectedValue === 'accumulated',
      formatNum: (value) => {
        if (isArpuType(dataType)) return formatSmallValue(value, 'decimals', 2);
        if (hasPercentList?.includes(dataType)) {
          return formatSmallPercent(value, 2);
        }
        return formatNum(value, false, userQuota.includes(dataType));

      },
    });
    opt = changeMin(opt, lineChartObj, {
      show: showChangeTag,
    });
    opt = xAxisHighLight(opt, {
      formatter: (value) => {
        const valueArr = value?.value?.split('-');
        if (type === DATE_DIMENSION.MONTH) {
          return `${valueArr?.[0]}-${valueArr?.[1]}`;
        }
        return `${valueArr?.[1]}-${valueArr?.[2]}`;

      },
    });

    opt.yAxis.axisLabel.formatter = (value) => {
      const formatLabel = value ? unitConversion(value, true) : value;
      let num;
      num =
        value < 1 && value > 0
          ? isArpuType(dataType)
            ? formatSmallValue(value, 'decimals', 2)
            : parseFloat(value.toFixed(3))
          : formatLabel;
      if (hasPercentList?.includes(dataType)) {
        if (value === 0) {
          num = '0%';
        }
        num = formatSmallPercent(value, 2, true).replace('.00%', '%');
      }
      if (showChangeTag && dataType === 'tru') {
        num = formatTruNum(value, true, 3);
      }
      return num;
    };
    // opt = formatyAxisSmallerThanOne(opt, hasPercentList?.includes(dataType))
    // // 事件会影响间距，有事件的时候间距太小（高亮时，横坐标也覆盖不全）
    // opt.xAxis[0].axisLabel.margin = 16
    // opt.tooltip.axisPointer.label.margin = 11
    if (regionDataType === 'os') {
      getSystemLegend(opt, selector);
      opt.legend.bottom = -4;
    }
    return chartOverlaySlot ? appendTopTipsEventForOverlay(opt) : opt;
  };
  // 柱状图图表配置
  const columunChartOtherConfig = (opt: any) => {
    opt.xAxis.data = data?.[CHART_TYPE.COLUMN]?.xAxis ?? [];
    opt.xAxis[0].axisLabel.formatter = function (value) {
      const valueArr = getDateFormatString(type, value).split('-');
      if (type === DATE_DIMENSION.MONTH) {
        return `${valueArr?.[0]}-${valueArr?.[1]}`;
      }
      return `${valueArr?.[1]}-${valueArr?.[2]}`;

    };
    opt.legend.formatter = (name) => name?.replace(/[SDLW|SDLM|SDLY|SWLM|SMLY]+/g, (matchStr) => {
      const map = {
        SDLW: 'Same Day Last Week',
        SDLM: 'Same Day Last Month',
        SDLY: 'Same Day Last Year',
        SWLM: 'Same Week Last Month',
        SMLY: 'Same Month Last Year',
      };
      return map[matchStr] || matchStr;
    });
    Object.keys(data?.[CHART_TYPE.COLUMN]?.yAxis ?? {})?.forEach((key) => {
      if (typeof opt.yAxis[key] === 'object') {
        opt.yAxis[key] = {
          ...opt.yAxis[key],
          ...data?.[CHART_TYPE.LINE]?.yAxis[key],
        };
      } else {
        opt.yAxis[key] = data?.[CHART_TYPE.LINE]?.yAxis[key];
      }
    });
    opt = addChartLabel(opt, {
      formatter: (params) => {
        if (isArpuType(dataType)) return formatSmallValue(params?.value, 'decimals', 2);
        const result =
          hasPercentList?.includes(dataType)
            ? formatSmallPercent(params?.value, 2)
            : formatNum(params?.value, false, userQuota.includes(dataType));
        if (!hasPercentList?.includes(dataType) && result?.length >= 9) {
          return unitConversionNew(params?.value, {
            isFloat: true,
            fixedNum: 3,
            fillZero: true,
          });
        }
        return result;
      },
      show: iseye && selectedValue === 'accumulated',
      formatNum: (value) => {
        if (isArpuType(dataType)) return formatSmallValue(value, 'decimals', 2);
        if (hasPercentList?.includes(dataType)) {
          return formatSmallPercent(value, 2);
        }
        return formatNum(value, false, userQuota.includes(dataType));

      },
    });
    opt = xAxisHighLight(opt, {
      formatter: (value) => {
        const valueArr = getDateFormatString(type, value?.value).split('-');
        if (type === DATE_DIMENSION.MONTH) {
          return `${valueArr?.[0]}-${valueArr?.[1]}`;
        }
        return `${valueArr?.[1]}-${valueArr?.[2]}`;

      },
    });
    opt.yAxis.axisLabel.formatter = (value) => {
      const formatLabel = value ? unitConversion(value, true) : value;
      let num;
      num = value < 1 && value > 0
        ? isArpuType(dataType)
          ? formatSmallValue(value, 'decimals', 2)
          : parseFloat(value.toFixed(3))
        : formatLabel;
      if (hasPercentList?.includes(dataType)) {
        if (value === 0) {
          num = '0%';
        }
        num = formatSmallPercent(value, 2, true).replace('.00%', '%');
      }
      if (showChangeTag && dataType === 'tru') {
        num = formatTruNum(value, true, 3);
      }
      return num;
    };
    // // 事件会影响间距，有事件的时候间距太小（高亮时，横坐标也覆盖不全）
    // opt.xAxis[0].axisLabel.margin = 16
    // opt.tooltip.axisPointer.label.margin = 11
    if (regionDataType === 'os') {
      getSystemLegend(opt, selector);
      opt.legend.bottom = -4;
    }
    return chartOverlaySlot ? appendTopTipsEventForOverlay(opt) : opt;
  };

  const htmlInitHeight = document.getElementsByClassName(`daily-segment-${renderIndex}`)?.[0]?.clientHeight || 0;
  useEffect(() => {
    setHeightFunc();
    // segementHeightRef.current = htmlInitHeight;
  }, [htmlInitHeight]);

  useEffect(() => {
    window.addEventListener('resize', setHeightFunc);
    return () => {
      window.removeEventListener('resize', setHeightFunc);
    };
  }, []);

  /** 全屏态切换 / 视口高度变化时主动触发图表高度重算（仅启用全屏能力时执行） */
  useEffect(() => {
    if (!viewportFullscreen?.enabled) return;
    setHeightFunc();
  }, [viewportFullscreen?.enabled, viewportFullscreen?.isFullScreen, viewportFullscreen?.viewportHeight]);

  /**
   * 用 ResizeObserver 监听 header 元素高度变化，解决 clientHeight 时机过早问题
   * （aggregateSubTitle 等异步内容渲染完成后，header 高度会变化，触发重新计算）
   */
  useEffect(() => {
    const cardBodyEl = document.getElementsByClassName(`dbd-daily-chart-card-body-${renderIndex}`)?.[0];
    const headerEl = cardBodyEl?.getElementsByClassName('dbd-overview-metrics-chart-header')?.[0];
    if (!headerEl) return;
    const observer = new ResizeObserver(() => {
      setHeightFunc();
    });
    observer.observe(headerEl);
    return () => {
      observer.disconnect();
    };
  }, [showTitle, renderIndex]);

  // 检测 Segment 是否超过三行，决定是否显示展开/收起按钮
  useEffect(() => {
    const segmentEl = segmentRef.current;
    if (!segmentEl) return;
    let rafId: number;
    const checkHeight = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const scrollH = segmentEl.scrollHeight;
        setShowExpandButton(scrollH > SEGMENT_THREE_ROW_HEIGHT);
      });
    };
    checkHeight();
    const observer = new ResizeObserver(checkHeight);
    observer.observe(segmentEl);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [segmentRef.current, tabList]);

  // 监听 Segment wrapper 高度变化，同步趋势图高度
  useEffect(() => {
    const wrapperEl = segmentRef.current?.parentElement;
    if (!wrapperEl) return;
    let rafId: number;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setHeightFunc();
      });
    });
    observer.observe(wrapperEl);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [segmentRef.current]);

  const setHeightFunc = () => {
    const cardBodyEl = document.getElementsByClassName(`dbd-daily-chart-card-body-${renderIndex}`)?.[0];
    const boxHeight = cardBodyEl?.clientHeight || 0;
    // Segment wrapper 高度（包含展开/收起按钮）
    const segmentWrapperHeight = cardBodyEl?.getElementsByClassName('dbd-overview-segment-wrapper')?.[0]?.clientHeight || 0;
    const htmlHeight = segmentWrapperHeight || (document.getElementsByClassName(`daily-segment-${renderIndex}`)?.[0]?.clientHeight || 0);

    /** 动态获取额外元素高度（Radio.Group、标题等），未渲染时为 0，不影响其他页面 */
    const radioGroupHeight = cardBodyEl?.getElementsByClassName('dbd-overview-metrics-chart__overlay-line-radio-group')?.[0]?.clientHeight || 0;
    const headerHeight = cardBodyEl?.getElementsByClassName('dbd-overview-metrics-chart-header')?.[0]?.clientHeight || 0;
    const extraHeight = radioGroupHeight + headerHeight;

    /** 动态获取 flex gap，无 gap 时为 0 */
    const contentEl = cardBodyEl?.querySelector('.tea-card__content');
    const gapValue = contentEl ? parseFloat(window.getComputedStyle(contentEl).rowGap) : NaN;
    const gap = isNaN(gapValue) ? 0 : gapValue;
    const extraGapCount = (radioGroupHeight > 0 ? 1 : 0) + (headerHeight > 0 ? 1 : 0);

    /** 全屏态下：以视口高度为基准，扣除 layer padding(32) + card 内边距 + segment / header / radio 高度 */
    const isFullScreenMode = !!(viewportFullscreen?.enabled && viewportFullscreen?.isFullScreen);
    const baseBoxHeight = isFullScreenMode && viewportFullscreen?.viewportHeight
      ? Math.max(0, viewportFullscreen.viewportHeight - 32)
      : boxHeight;

    // 40为padding、36为筛选器一行的高度
    const newHeight = baseBoxHeight - 40 - htmlHeight - 36 - extraHeight - (extraGapCount + 2) * gap;
    // 高度未变化时不触发重渲染，避免 ResizeObserver 循环振荡
    if (Math.abs(newHeight - prevHeightRef.current) < 1) return;
    prevHeightRef.current = newHeight;
    resizeNum.current = resizeNum.current + 1;
    setdheight(newHeight);
  };

  // 处理表格列配置，为 header 添加溢出检测
  const processedTableOption = useMemo(() => {
    const tableOption = data?.[CHART_TYPE.TABLE]?.option || [];
    return tableOption.map((column: any) => {
      // 如果 header 是字符串，包装成带溢出检测的组件
      if (typeof column.header === 'string') {
        return {
          ...column,
          header: <OverflowBubbleText text={column.header} placement="top" />,
        };
      }
      return column;
    });
  }, [data?.[CHART_TYPE.TABLE]?.option]);

  const subTitleComponent = useMemo(() => {
    return (dimensionOptions?.find(i => i?.value === selectedValue)?.showAggregate !== false && (
      <div className={classNames("dbd-overview-metrics-chart__aggregate-desc", {
        'dbd-overview-metrics-chart__aggregate-desc--loading': loading,
      })}>
        {aggregateSubTitle.map((item, idx) => (
          <span key={idx} className="dbd-overview-metrics-chart__aggregate-desc-item">
            {item.name}: {loading ? '-' : item.num}
          </span>
        ))}
      </div>
    ));
  }, [aggregateSubTitle, loading, selectedValue]);

  const fsOverlayXAxis = useMemo((): string[] => {
    const raw =
      comType === 'column'
        ? data?.[CHART_TYPE.COLUMN]?.xAxis ?? []
        : data?.[CHART_TYPE.LINE]?.xAxis ?? [];
    return Array.isArray(raw) ? raw.map(v => String(v)) : [];
  }, [comType, data, CHART_TYPE.COLUMN, CHART_TYPE.LINE]);

  const chartOverlayContext = useMemo((): IChartOverlaySlotContext => {
    const xSig = fsOverlayXAxis.join(',');
    return {
      getChart: () => {
        if (comType === 'column') {
          return barChartRef.current?.instance() ?? null;
        }
        return lineChartRef.current?.instance() ?? null;
      },
      xAxisData: fsOverlayXAxis,
      chartLoading: !!loading,
      layoutKey: loading
        ? `loading|${selectedValue}|${comType}|${dataType}`
        : `${selectedValue}|${comType}|${dataType}|${xSig}`,
    };
  }, [
    comType,
    dataType,
    fsOverlayXAxis,
    loading,
    selectedValue,
  ]);

  const resolvedChartOverlay = useMemo(() => {
    if (chartOverlaySlot == null) {
      return null;
    }
    if (typeof chartOverlaySlot === 'function') {
      return chartOverlaySlot(chartOverlayContext);
    }
    return chartOverlaySlot;
  }, [chartOverlaySlot, chartOverlayContext]);

  const isViewportFullScreen = !!(
    viewportFullscreen?.enabled && viewportFullscreen?.isFullScreen
  );
  const fsEventFilterBesideTitle =
    isViewportFullScreen && eventFilterBesideTitle;
  const fsEventFilterBesideSelect =
    isViewportFullScreen && !eventFilterBesideTitle;
  const showSegmentTabs =
    !isViewportFullScreen || showSegmentTabsInFullScreen;

  /**
   * 全屏事件筛选在非全屏时仍保留 DOM（隐藏 keeper），避免 KpiEventFilter 卸载导致
   * EventSettingModal 勾选态被重置。挂载位置由 eventFilterBesideTitle 固定（全屏切换不换位）。
   */
  const renderPersistentEventFilterSlot = (
    visible: boolean,
  ): React.ReactNode => {
    if (leftExtraSlot == null) {
      return null;
    }
    return (
      <span
        className={classNames('dbd-overview-fs-event-filter-slot', {
          'dbd-overview-fs-event-filter-mount-keeper': !visible,
        })}
      >
        {leftExtraSlot}
      </span>
    );
  };

  return (
    <Card className="overlay-line overlay-line-daily">
      <Card.Body className={`dbd-daily-chart-card-body-card dbd-daily-chart-card-body-${renderIndex}`}>
        {showSegmentTabs && (
          <div
            className={classNames('dbd-overview-segment-wrapper', {
              'dbd-overview-segment-wrapper--fullscreen': isViewportFullScreen,
            })}
          >
            <div
              ref={segmentRef}
              className={`dbd-overview-segment-wrapper__segment ${isExpanded || !showExpandButton ? 'dbd-overview-segment-wrapper__segment--show' : 'dbd-overview-segment-wrapper__segment--hide'}`}
            >
              <Segment
                className={`overlay-line-tab dbo-overlay-style daily-segment-${renderIndex}`}
                value={dataType}
                onChange={(value) => {
                  onDataTypeChange(
                    value,
                    tabList?.find((tab) => tab.value === value)?.text || '',
                    selectedValue,
                  );
                }}
                options={segementList}
              />
            </div>
            {showExpandButton && (
              <span
                className="dbd-overview-segment-wrapper__btn"
                onClick={() => {
                  setIsExpanded(!isExpanded);
                }}
              >
                <SvgIcon
                  className={`dbd-overview-segment-wrapper__arrow ${isExpanded ? 'dbd-overview-segment-wrapper__arrow--up' : ''}`}
                  type={isExpanded ? 'point-top' : 'point-down'}
                />
              </span>
            )}
          </div>
        )}
        {config?.dimensionSelectType === 'radio' ? (
          <Radio.Group
            className="dbd-overview-metrics-chart__overlay-line-radio-group"
            value={selectedValue}
            onChange={(value) => {
              const selectedOption = separatedOption?.find((item) => item?.value === value);
              setSelectText(selectedOption?.text || '');
              setSelectedValue(value);
              onDataTypeChange(
                dataType,
                tabList?.find((tab) => tab.value === dataType)?.text || '',
                value,
              );
            }}
          >
            {separatedOption?.map((item) => (
              <Radio key={item?.value} name={item?.value}>
                {item?.text}
              </Radio> 
            ))}
          </Radio.Group>
        ) : null}
        {(!!showTitle) && (
          <OverviewMetricsChartHeader
            className={
              fsEventFilterBesideTitle
                ? 'dbd-overview-metrics-chart-header--event-filter-beside-title'
                : undefined
            }
            titleEn={currentTab?.text || ''}
            subTitleComponent={subTitleComponent}
            config={config}
            titleRightSlot={
              eventFilterBesideTitle
                ? renderPersistentEventFilterSlot(fsEventFilterBesideTitle)
                : undefined
            }
          />
        )}
        <div className='"overlay-line overlay-line-daily'>
          <Justify
            left={
              <>
                {[undefined, null, 'dropdown'].includes(config?.dimensionSelectType) && (
                  <Select
                    listHeight={226}
                    appearance="default"
                    className="overlay-line-select-text"
                    options={separatedOption}
                    value={selectedValue}
                    onChange={(value, e) => {
                      setSelectText(e.option.text);
                      setSelectedValue(value);
                      onDataTypeChange(
                        dataType,
                        tabList?.find((tab) => tab.value === dataType)?.text || '',
                        value,
                      );
                    }}
                    placeholder="accumulated"
                  />
                )}
                {!eventFilterBesideTitle &&
                  renderPersistentEventFilterSlot(fsEventFilterBesideSelect)}
              </>
            }
            right={
              hasDataFlag && (
                <div className="overlay-line-flex">
                  {comType === 'sheet' ||
                    selectedValue !== 'accumulated' ? null : (
                      <Bubble
                        dark
                        placement="top"
                        content={
                          iseye
                            ? formatMessage({ id: 'common_show_values' })
                            : formatMessage({ id: 'common_hide_values' })
                        }
                      >
                        <div
                          className="overlay-line-item"
                          onClick={() => {
                            setIseye(!iseye);
                          }}
                        >
                          <SvgIcon
                            className="overlay-line-icon"
                            type={iseye ? 'open-eyes' : 'eyes-closed'}
                          />
                        </div>
                      </Bubble>
                    )}
                  {rightIcon.map((e) => {
                    const isActive =
                      e.type == comType ||
                      (comType === 'discount' &&
                        e.type === 'turn' &&
                        showChangeTag);
                    if (comType !== 'discount' && e.type === 'turn') {
                      return;
                    }
                    return (
                      <Bubble
                        key={e.type}
                        dark
                        placement="top"
                        content={e.Bubble}
                      >
                        <div
                          className="overlay-line-item"
                          onClick={() => iconClickfunc(e)}
                        >
                          <ACard
                            url={e.type === 'vs' ? `/dashboard/game/${gameCode}/comparisonAnalysis/dateComparison` : ''}
                            isStopPropagation={false}>
                            <SvgIcon
                              className={
                                isActive
                                  ? 'overlay-line-action'
                                  : 'overlay-line-icon'
                              }
                              type={e.icon}
                            />
                          </ACard>
                        </div>
                      </Bubble>
                    );
                  })}
                  {!hasDataFlag ? null : (
                    <Bubble dark placement="top" content="">
                      <i onClick={downloadData}>
                        <SvgIcon className="overlay-line-down" type="download" />
                      </i>
                    </Bubble>
                  )}
                  {viewportFullscreen?.enabled ? (
                    <ViewportFullscreenTip
                      className="overlay-line-fullscreen-tip"
                      isFullScreen={viewportFullscreen.isFullScreen}
                      requestFullScreen={viewportFullscreen.requestFullScreen}
                      exitFullScreen={viewportFullscreen.exitFullScreen}
                      disabled={isViewportFullscreenEnterDisabled}
                    />
                  ) : null}
                </div>
              )
            }
          />
          <Row>
            {comType === 'column' ? (
              <Col>
                {/* viewportFullscreen?.isFullScreen */}
                <div className={
                  classNames("dbd-overlay-chart",
                    { "dbd-overlay-chart--fullscreen": !!(viewportFullscreen?.enabled && viewportFullscreen?.isFullScreen) }
                  )
                }>
                  <Abnormal
                    isLoading={loading}
                    isError={error}
                    isNoData={!hasDataFlag}
                    style={{ height: dheight }}
                    component={
                      <BarChart
                        key={
                          JSON.stringify(data?.[CHART_TYPE.LINE]?.series) +
                          iseye +
                          dheight + JSON.stringify(formValue) + JSON.stringify(eventData) +
                          resizeNum.current
                        }
                        ref={barChartRef}
                        height={dheight}
                        title={''}
                        legendData={data?.[CHART_TYPE.COLUMN]?.legend ?? []}
                        selectedLegendData={data?.[CHART_TYPE.COLUMN]?.selectedLegend ?? {}}
                        xAxisData={data?.[CHART_TYPE.COLUMN]?.xAxis ?? []}
                        series={data?.[CHART_TYPE.COLUMN]?.series ?? []}
                        formatterFunc={(params: any) =>
                          selectedValue === 'accumulated'
                            ? formatterFunc(params)
                            : dimensionFormatterFunc(params)
                        }
                        setOtherConfigFunc={(opt: any) =>
                          columunChartOtherConfig(opt)
                        }
                        chartEventConfig={(viewportFullscreen?.enabled && viewportFullscreen?.isFullScreen) ? undefined : eventSlotConfigFunc}
                      />
                    }
                  />
                  {!(viewportFullscreen?.enabled && viewportFullscreen?.isFullScreen) && (
                    <EventDetailsPop
                      className={"dbd-event-detail-pop"}
                      key={JSON.stringify(formValue) + JSON.stringify(currentEvents)}
                      isOverview={true}
                      currentEvents={currentEvents}
                      pathname={linkPath}
                      timeType={formValue?.timeType}
                      popRef={popRef}
                      eventTypeMaxCount={eventTypeMaxCount}
                    />
                  )}
                  {resolvedChartOverlay}
                </div>
              </Col>
            ) : (
              <></>
            )}
            {comType === 'discount' ? (
              <Col>
                <div className={
                  classNames("dbd-overlay-chart",
                    {"dbd-overlay-chart--fullscreen": !!(viewportFullscreen?.enabled && viewportFullscreen?.isFullScreen) }
                  )
                }>
                  <Abnormal
                    isLoading={loading}
                    isError={error}
                    isNoData={!hasDataFlag}
                    style={{ height: dheight }}
                    component={
                      <>
                        <LineChart
                          height={dheight}
                          ref={lineChartRef}
                          key={
                            JSON.stringify(data?.[CHART_TYPE.LINE]?.series) +
                            iseye +
                            showChangeTag +
                            dheight + JSON.stringify(formValue) + JSON.stringify(eventData) +
                            resizeNum.current
                          }
                          title={''}
                          legendData={data?.[CHART_TYPE.LINE]?.legend ?? []}
                          selectedLegendData={data?.[CHART_TYPE.LINE]?.selectedLegend ?? {}}
                          xAxisData={data?.[CHART_TYPE.LINE]?.xAxis ?? []}
                          series={data?.[CHART_TYPE.LINE]?.series ?? []}
                          formatterFunc={(params: any) =>
                            selectedValue === 'accumulated'
                              ? formatterFunc(params)
                              : dimensionFormatterFunc(params)
                          }
                          setOtherConfigFunc={(opt: any) => lineChartOtherConfig(opt)}
                          chartEventConfig={(viewportFullscreen?.enabled && viewportFullscreen?.isFullScreen) ? undefined : eventSlotConfigFunc}
                        />
                        {placeholderId && (
                          <div
                            id={placeholderId}
                            className="dtd-chart-x-placehoder"
                          ></div>
                        )}
                      </>
                    }
                  />
                  {/* <EventsPopover /> */}
                  {!(viewportFullscreen?.enabled && viewportFullscreen?.isFullScreen) && (
                    <EventDetailsPop
                      className={"dbd-event-detail-pop"}
                      key={JSON.stringify(formValue) + JSON.stringify(currentEvents)}
                      isOverview={true}
                      currentEvents={currentEvents}
                      pathname={linkPath}
                      timeType={formValue?.timeType}
                      popRef={popRef}
                      eventTypeMaxCount={eventTypeMaxCount}
                    />
                  )}
                  {resolvedChartOverlay}
                </div>
              </Col>
            ) : (
              <></>
            )}

            {comType === 'sheet' ? (
              <Col>
                <Abnormal
                  isLoading={loading}
                  isError={error}
                  isNoData={!hasDataFlag}
                  style={{ height: dheight }}
                  component={
                    <MiniTable
                      loading={loading}
                      height={dheight}
                      tableOption={processedTableOption}
                      tableList={data?.[CHART_TYPE.TABLE]?.data}
                      recordKey={'date'}
                    />
                  }
                />
              </Col>
            ) : (
              <></>
            )}
          </Row>
        </div>
      </Card.Body>
    </Card>
  );
}
